import React, { ReactNode } from 'react';

import {
  MessageFormatter,
  pluralTypeHandler,
  selectTypeHandler,
} from '@ultraq/icu-message-formatter';

import {
  LOCALES,
  Messages,
  TranslationKey,
  TranslationOptions,
  TranslationParams,
} from 'state/intl/types';
import en from 'state/translations/en.json';
import { getLocale } from 'utils/intl/language';

import { clearFormattersCache } from './formatters';

const formatterConfig = { plural: pluralTypeHandler, select: selectTypeHandler };

// Cache regex for performance to avoid recreation on every tRich call
const REACT_NODE_PLACEHOLDER_REGEX = /(__REACT_NODE_.+?__)/g;
let currentMessages: Messages = en;
let currentLocaleKey: string | null = getLocale();
let currentFormatter: MessageFormatter | null = null;
let cachedFormatterLocale: string | null = null;
const loggedWarnings = new Set<string>();

/**
 * Get or create cached MessageFormatter instance for current locale
 *
 * The formatter is cached to avoid recreating it on every translation call,
 * which would be expensive since MessageFormatter needs to parse locale rules.
 * The cache is invalidated by syncTranslationState when the locale changes.
 */
const getFormatter = (locale: string): MessageFormatter => {
  if (!currentFormatter || cachedFormatterLocale !== locale) {
    try {
      currentFormatter = new MessageFormatter(locale, formatterConfig);
      cachedFormatterLocale = locale;
    } catch (e) {
      // Fallback if something goes wrong with locale
      currentFormatter = new MessageFormatter(LOCALES.en_us, formatterConfig);
      cachedFormatterLocale = LOCALES.en_us;
    }
  }

  return currentFormatter;
};

/**
 * Sync the global translation state
 */
export const syncTranslationState = (locale: string, messages: Messages): void => {
  currentMessages = messages;

  if (currentLocaleKey !== locale) {
    currentLocaleKey = locale;

    // Invalidate formatter cache when locale changes
    currentFormatter = null;
    cachedFormatterLocale = null;
    clearFormattersCache();
  }
};

/**
 * Normalizes translation options to a consistent format.
 *
 * This function unifies different ways of passing options:
 * - If it's a string → treats it as defaultValue
 * - If it's an object → treats it as params mixed with defaultValue
 *
 * Examples:
 *   normalizeOptions("fallback") → { defaultValue: "fallback" }
 *   normalizeOptions({ key: "value" }) → { params: { key: "value" } }
 *   normalizeOptions({ key: "value", defaultValue: "fallback" }) → { params: { key: "value" }, defaultValue: "fallback" }
 */
const normalizeOptions = (
  options?: TranslationOptions
): { params?: TranslationParams; defaultValue?: string } => {
  if (!options) {
    return {};
  }
  if (typeof options === 'string') {
    return { defaultValue: options };
  }
  if ('params' in options) {
    return options as { params?: TranslationParams; defaultValue?: string };
  }
  if ('defaultValue' in options) {
    const { defaultValue, ...rest } = options as Record<string, any>;
    return {
      defaultValue: defaultValue as string,
      params: Object.keys(rest).length > 0 ? (rest as TranslationParams) : undefined,
    };
  }
  // Legacy format: options object is treated as params
  return { params: options as TranslationParams };
};

/**
 * Main translation function
 * @param key - The translation key
 * @param {TranslationOptions} options - The translation options: params, defaultValue, or a string shorthand for defaultValue when no template is needed
 * @returns The translated string
 */
export function t(key: string, defaultValue: string): string;
export function t(
  key: string,
  options: { defaultValue: string; [key: string]: string | number | null | undefined }
): string;
export function t<K extends TranslationKey>(key: K, options?: TranslationOptions): string;
export function t(key: string, options?: TranslationOptions): string {
  // Attempt to get template first to check for placeholders early to avoid normalizing options when not needed
  const rawTemplate: string = currentMessages[key as keyof typeof currentMessages];

  // Fast path: if template exists and has no placeholders, return early
  if (rawTemplate && !rawTemplate.includes('{')) {
    return rawTemplate;
  }

  const { params, defaultValue } = normalizeOptions(options);

  if (!rawTemplate) {
    if (__DEV__) {
      const message = `Translation key "${key}" not found for locale "${currentLocaleKey}"`;
      if (!loggedWarnings.has(message)) {
        // eslint-disable-next-line no-console
        console.warn(message);
        loggedWarnings.add(message);
      }
    }
    // If defaultValue is provided and has placeholders, try to format it
    if (defaultValue && params && defaultValue.includes('{')) {
      try {
        const formatter = getFormatter(currentLocaleKey || LOCALES.en_us);
        return formatter.format(defaultValue, params);
      } catch (error) {
        if (__DEV__) {
          const message = `Error formatting defaultValue for key "${key}":`;
          if (!loggedWarnings.has(message)) {
            // eslint-disable-next-line no-console
            console.warn(message, error);
            loggedWarnings.add(message);
          }
        }
      }
    }

    // This is helpful when the translation key was added only for one locale and not yet translated.
    return defaultValue || key;
  }

  // At this point the template has placeholders, so we need to normalize the options to a consistent format.

  // Fast path: no params to fill the placeholders, return fallback before try to format
  // ie: t('justMoreToReachNextLevel', { points: null }) should return 'Just {points} more to reach the next level'
  if (
    !params ||
    Object.keys(params).length === 0 ||
    Object.values(params).every(value => value === null || value === undefined)
  ) {
    return defaultValue || rawTemplate || key;
  }

  // Format with params
  try {
    const formatter = getFormatter((currentLocaleKey || LOCALES.en_us) as string);
    return formatter.format(rawTemplate, params || {});
  } catch (error) {
    if (__DEV__) {
      const message = `Error formatting translation for key "${key}":`;
      if (!loggedWarnings.has(message)) {
        // eslint-disable-next-line no-console
        console.warn(message, error);
        loggedWarnings.add(message);
      }
    }
    return defaultValue || rawTemplate || key;
  }
}

/**
 * Rich Text support (e.g. for React components in translations)
 */
export const tRich = <K extends TranslationKey>(
  key: K,
  params?: Record<string, ReactNode | string | number | null | undefined>,
  options?: { defaultValue?: string }
): ReactNode => {
  if (!params) {
    return t(key, options);
  }

  const stringParams: TranslationParams = {};
  const reactNodes: Record<string, ReactNode> = {};
  let hasReactNode = false;

  // Iterate over all keys to include undefined values (Object.entries skips undefined)
  for (const k of Object.keys(params)) {
    const v = params[k];
    if (React.isValidElement(v) || (Array.isArray(v) && v.some(React.isValidElement))) {
      const placeholder = `__REACT_NODE_${k}__`;
      reactNodes[placeholder] = v;
      stringParams[k] = placeholder;
      hasReactNode = true;
    } else {
      // Preserve null and undefined values so createSafeParams can handle them
      stringParams[k] = v as string | number | null | undefined;
    }
  }

  // Fast path: no React nodes, just use regular translation
  if (!hasReactNode) {
    return t(key, { params: stringParams, defaultValue: options?.defaultValue });
  }

  // Format and split by placeholders to reconstruct with React nodes
  const formatted = t(key, { params: stringParams, defaultValue: options?.defaultValue });
  const parts = formatted.split(REACT_NODE_PLACEHOLDER_REGEX);

  return parts.map((part, index) => {
    const node = reactNodes[part];
    return node ? React.createElement(React.Fragment, { key: index }, node) : part;
  });
};

// Re-export formatters from formatters.ts for backward compatibility
export { formatDate, formatTime, formatDateToParts, formatNumber } from './formatters';
